import { TextField, TextFieldProps } from '@mui/material';
import {
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  get,
} from 'react-hook-form';

/**
 * RHF-aware MUI TextField.
 *
 * Replaces:
 *   <TextField
 *     {...register('field')}
 *     error={!!errors.field}
 *     helperText={errors.field?.message}
 *   />
 *
 * with:
 *   <FormTextField name="field" register={register} errors={errors} label="..." />
 *
 * Supports nested paths (e.g. "address.city") via react-hook-form's `get` helper.
 */
type Props<T extends FieldValues> = Omit<TextFieldProps, 'name' | 'error'> & {
  name: Path<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  /** Optional external error flag — ORed with the RHF field error. */
  error?: boolean;
};

export function FormTextField<T extends FieldValues>({
  name,
  register,
  errors,
  helperText,
  error: externalError,
  fullWidth = true,
  ...rest
}: Props<T>) {
  // `get` walks dotted paths so `errors["address.city"]` works.
  const fieldError = get(errors, name) as { message?: string } | undefined;
  const hasError = !!fieldError || !!externalError;
  return (
    <TextField
      {...rest}
      {...register(name)}
      fullWidth={fullWidth}
      error={hasError}
      helperText={fieldError?.message ?? helperText}
    />
  );
}
